import { Word, UserProgress } from '../types';
import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { endBefore, equalTo, get, limitToLast, orderByChild, query, ref, set, update } from 'firebase/database';
import { themes } from '../utils/themes';
import { db } from '../lib/firebase';
import { User } from 'firebase/auth';
import { AchievementService, UserAchievements } from '../services/achievementService';
import { sanitizeFirebaseKey } from '../utils/firebaseUtils';

interface Store {
  savedWords: Word[];
  updateWordProgress: (wordId: string, progress: number) => void;
  getWords: () => void;
  getWordsPaginated: (loadMore?: boolean) => void
}

export const UserContext = createContext<{
  savedWords: Word[];
  savedExpressions: Word[];
  updateWordProgress: (wordId: string, themeName?: string) => void;
  getWords: () => void;
  userProgress: UserProgress;
  addWord: (wordData, themeName?: string) => void;
  addExpression: (expressionData, themeName?: string) => void;
  getWordsPaginated: (loadMore?: boolean) => void
  getExpressionsPaginated: (loadMore?: boolean) => void
  loadMore: boolean,
  loadMoreExpressions: boolean,
  loadingWords: boolean,
  loadingExpressions: boolean,
  deleteWord: (wordId: string) => void
  deleteExpression: (expressionId: string) => void
  updateWordTimestamp: (wordId: string) => void
  getThemes: () => void
  themesProgress: any
  getWordsFromTheme: (theme: string) => void
  getWord: (word: string) => boolean | Word
  getExpression: (word: string) => boolean | Word
  wordsTheme: Word[]
  saveRateLimitAI: () => void,
  saveUser: (name: string, currentUser: User, email: string) => void
  getDayExercise: (activity: string, word: object) => Promise<boolean>,
  saveDayExercise: (exerciseName: string) => void,
  getAllDayExercises: (currentWord: any) => Promise<boolean>,
  checkAchievements: () => Promise<{ achievements: any[], badges: any[] }>,
  userAchievements: UserAchievements,
  newAchievements: { achievements: any[], badges: any[] },
  wordOfDayPoints: { speak: number, fillBlank: number, synonym: number, trueFalse: number, podcast: number },
  setWordOfDayPoints: (updater: (prev: any) => any) => void
}>({
  savedWords: [],
  savedExpressions: [],
  updateWordProgress: () => {},
  getWords: () => {},
  userProgress: {},
  addWord: () => {},
  addExpression: () => {},
  getWordsPaginated: () => {},
  getExpressionsPaginated: () => {},
  loadMore: true,
  loadMoreExpressions: true,
  loadingWords: true,
  loadingExpressions: true,
  deleteWord: () => {},
  deleteExpression: () => {},
  updateWordTimestamp: () => {},
  getThemes: () => {},
  themesProgress: [],
  getWordsFromTheme: () => {},
  getWord: () => {},
  getExpression: () => {},
  wordsTheme: [],
  saveRateLimitAI: () => {},
  saveUser: () => {},
  getDayExercise: async () => false,
  saveDayExercise: () => {},
  getAllDayExercises: async () => false,
  checkAchievements: async () => ({ achievements: [], badges: [] }),
  userAchievements: { achievements: [], badges: [], totalPoints: 0, unlockedCount: 0 },
  newAchievements: { achievements: [], badges: [] },
  wordOfDayPoints: { speak: 0, fillBlank: 0, synonym: 0, trueFalse: 0, podcast: 0 },
  setWordOfDayPoints: () => {}
});

let lastVisibleCreatedAt = null; // Track the last retrieved `createdAt` value
let lastVisibleExpressionCreatedAt = null;
const pageSize = 20;

export function UserContextProvider({ children } : { children: React.ReactNode }) {
  const [savedWords, setSavedWords] = useState<Word[]>([]);
  const [savedExpressions, setSavedExpressions] = useState<Word[]>([]);
  const [themesProgress, setThemesProgress] = useState([]);
  const [loadMore, setLoadMore] = useState(true);
  const [loadMoreExpressions, setLoadMoreExpressions] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingExpressions, setLoadingExpressions] = useState(true);
  const [wordsTheme, setWordsTheme] = useState<Word[]>([]);
  const [userProgress, setUserProgress] = useState<UserProgress>({
    wordsLearned: 0,
    streak: 0,
    lastPractice: undefined,
  });
  const [userAchievements, setUserAchievements] = useState<UserAchievements>({
    achievements: [],
    badges: [],
    totalPoints: 0,
    unlockedCount: 0
  });
  const [newAchievements, setNewAchievements] = useState<{ achievements: any[], badges: any[] }>({
    achievements: [],
    badges: []
  });
  const [wordOfDayPoints, setWordOfDayPoints] = useState({ speak: 0, fillBlank: 0, synonym: 0, trueFalse: 0, podcast: 0 });
  
  // Client-side pagination states
  const [allSavedWords, setAllSavedWords] = useState<Word[]>([]);
  const [allSavedExpressions, setAllSavedExpressions] = useState<Word[]>([]);
  const [wordPage, setWordPage] = useState(1);
  const [expressionPage, setExpressionPage] = useState(1);
  
  const { currentUser } = useContext(AuthContext)
  const hasCheckedAchievements = useRef(false);

  const getWord = async (word: string) => {
    if (!currentUser?.uid || !word) return false;
    const safeKey = sanitizeFirebaseKey(word);
    if (!safeKey) return false;
    try {
      const wordRef = ref(db, `savedWords/${currentUser?.uid}/${safeKey}`);
      const snapshot = await get(wordRef);
      if(snapshot.exists() === false) return false;
      const data = snapshot.val();
      return data;
    } catch (err) {
      console.error('Error getting word:', err);
      return false;
    }
  }

  const getExpression = async (word: string) => {
    if (!currentUser?.uid || !word) return false;
    const safeKey = sanitizeFirebaseKey(word);
    if (!safeKey) return false;
    try {
      const expRef = ref(db, `savedExpressions/${currentUser?.uid}/${safeKey}`);
      const snapshot = await get(expRef);
      if(snapshot.exists() === false) return false;
      const data = snapshot.val();
      return data;
    } catch (err) {
      console.error('Error getting expression:', err);
      return false;
    }
  }

  const checkIfWordExistsInThemes = (word) => {
    const theme = themes.find(theme => theme.words.includes(word));
    if(theme) {
      return theme.name
    } else {
      return false
    }
  }

  const saveRateLimitAI = async () => {
    const rateLimitRef = ref(db, `rateLimit/${Date.now()}`);
    await set(rateLimitRef, {
      user: currentUser?.uid
    })
  }
  
  const addWord = async (wordData, themeName=null) => {
    try {
      if (!currentUser?.uid) return;
      const rawWord = wordData?.word ? wordData.word : wordData;
      if (!rawWord) return;
      const safeKey = sanitizeFirebaseKey(rawWord);
      if (!safeKey) return;
      const wordRef = ref(db, `savedWords/${currentUser?.uid}/${safeKey}`);
      const wordToSave: any = {
        word: rawWord,
        createdAt: Date.now(),
        lastPractice: Date.now(),
        progress: 0,
        isAutoAdded: false
      };

      if (wordData?.type) wordToSave.type = wordData.type;
      if (wordData?.literalMeaning) wordToSave.literalMeaning = wordData.literalMeaning;
      if (wordData?.exampleTranslation) wordToSave.exampleTranslation = wordData.exampleTranslation;
      if (wordData?.exampleSentence) wordToSave.exampleSentence = wordData.exampleSentence;
      if (wordData?.translation) wordToSave.translation = wordData.translation;

      themeName = themeName || checkIfWordExistsInThemes(rawWord)
      if(themeName){
        wordToSave.theme = themeName
      }

      const snapshot = await get(wordRef);
      if(snapshot.exists()) {
        const existingData = snapshot.val();
        if (existingData.isAutoAdded) {
          await set(wordRef, { ...existingData, ...wordToSave, isAutoAdded: false });
          const newWordList = [wordToSave, ...allSavedWords.filter(w => w.word !== wordToSave.word)].sort((a: any, b: any) => b.createdAt - a.createdAt);
          setAllSavedWords(newWordList);
          setSavedWords(newWordList.slice(0, wordPage * pageSize));
        }
        return
      }

      await set(wordRef, wordToSave);
      const newWordList = [wordToSave, ...allSavedWords].sort((a: any, b: any) => b.createdAt - a.createdAt);
      setAllSavedWords(newWordList);
      setSavedWords(newWordList.slice(0, wordPage * pageSize));
      setUserProgress(prev => ({
        ...prev,
        wordsLearned: prev?.wordsLearned ? prev.wordsLearned + 1 : 1
      }))
      await updateUserProgress(true)
    } catch (err) {
      console.error('Error adding word:', err);
      throw err
    }
  }

  const addExpression = async (expressionData, themeName=null) => {
    try {
      if (!currentUser?.uid) return;
      const rawExp = expressionData?.word ? expressionData.word : expressionData;
      if (!rawExp) return;
      const safeKey = sanitizeFirebaseKey(rawExp);
      if (!safeKey) return;
      const expRef = ref(db, `savedExpressions/${currentUser?.uid}/${safeKey}`);
      const expToSave: any = {
        word: rawExp,
        createdAt: Date.now(),
        lastPractice: Date.now(),
        progress: 0,
        isAutoAdded: false
      };

      if (expressionData?.type) expToSave.type = expressionData.type;
      if (expressionData?.literalMeaning) expToSave.literalMeaning = expressionData.literalMeaning;
      if (expressionData?.exampleTranslation) expToSave.exampleTranslation = expressionData.exampleTranslation;
      if (expressionData?.exampleSentence) expToSave.exampleSentence = expressionData.exampleSentence;
      if (expressionData?.translation) expToSave.translation = expressionData.translation;

      if(themeName){
        expToSave.theme = themeName;
      }

      const snapshot = await get(expRef);
      if(snapshot.exists()) {
        const existingData = snapshot.val();
        if (existingData.isAutoAdded) {
          await set(expRef, { ...existingData, ...expToSave, isAutoAdded: false });
          const newExpList = [expToSave, ...allSavedExpressions.filter(e => e.word !== expToSave.word)].sort((a: any, b: any) => b.createdAt - a.createdAt);
          setAllSavedExpressions(newExpList);
          setSavedExpressions(newExpList.slice(0, expressionPage * pageSize));
        }
        return;
      }

      await set(expRef, expToSave);
      const newExpList = [expToSave, ...allSavedExpressions].sort((a: any, b: any) => b.createdAt - a.createdAt);
      setAllSavedExpressions(newExpList);
      setSavedExpressions(newExpList.slice(0, expressionPage * pageSize));
      // Optional: you can update user progress for expressions learned as well
    } catch (err) {
      console.error('Error adding expression:', err);
      throw err;
    }
  }

  const updateWordProgress = async (word: string, theme?: string) => {
    if (!currentUser?.uid || currentUser === null || currentUser === undefined) return;
    if(currentUser.uid === undefined || !word) return;

    checkIfUserExistsAndCreate()

    const safeKey = sanitizeFirebaseKey(word);
    if (!safeKey) return;

    const wordRef = ref(db, `savedWords/${currentUser?.uid}/${safeKey}`);
    const snapshot = await get(wordRef);
    let data = snapshot.val();
    if(snapshot.exists() === false) {
      data = {
        word: word,
        createdAt: Date.now(),
        lastPractice: Date.now(),
        progress: 0,
        isAutoAdded: true
      };
    }

    theme = theme || checkIfWordExistsInThemes(word)

    const oldWordProgress = data.progress || 0;

    if (oldWordProgress < 8) {
      if(theme) {
        data.theme = theme;
        const themeRef = ref(db, `users/${currentUser?.uid}/themes/${theme}`);
        const themeSnapshot = await get(themeRef);
        if(themeSnapshot.exists()) {
          const themeData = themeSnapshot.val();
          themeData.progress = themeData.progress + 1;
          themeData.lastPractice = Date.now();
          await set(themeRef, themeData)
          setThemesProgress((prev) => prev.map(t => t.theme === theme ? themeData : t))
        } else {
          const themeData = {
            theme: theme,
            progress: 1,
            createdAt: Date.now(),
            lastPractice: Date.now(),
          }
          await set(themeRef, themeData)
          setThemesProgress((prev) => {
            const exists = prev.some((t: any) => t.theme === theme);
            if (exists) return prev.map((t: any) => t.theme === theme ? themeData : t);
            return [...prev, themeData];
          });
        }
      }
      data.progress = oldWordProgress + 1;
    }

    data.lastPractice = Date.now();
    await set(wordRef, data)
    await updateUserProgress(false)
  }

  const checkIfUserExistsAndCreate = async () => {
    if(!currentUser?.uid) return
    const userRef = ref(db, `users/${currentUser?.uid}`);
    const snapshot = await get(userRef);
    if(snapshot.exists() === false) {
      const data = {
        wordsLearned: 0,
        lastPractice: Date.now(),
        email: currentUser?.email,
        username: currentUser?.displayName || String(currentUser?.email).split('@')[0]
      }
      await set(userRef, data)
    }
  }

  const updateUserProgress = async (updateWordsLearned=false) => {
    checkIfUserExistsAndCreate()
    const userRef = ref(db, `users/${currentUser?.uid}`);
    const snapshot = await get(userRef);
    if(snapshot.exists() === false) {
      const data = {
        wordsLearned: updateWordsLearned ? 1 : 0,
        lastPractice: Date.now(),
        email: currentUser?.email,
        streak: 1,
        totalDaysStudied: 1
      }
      await set(userRef, data)
    } else {
      const data = snapshot.val();
      
      let newStreak = data.streak || 0;
      let totalDaysStudied = data.totalDaysStudied;
      if (totalDaysStudied === undefined) {
        totalDaysStudied = Math.max(Object.keys(data.practiceHistory || {}).length, data.streak || 0);
      }

      if (data.lastPractice) {
        const lastDate = new Date(data.lastPractice);
        lastDate.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const diffDays = Math.round((today.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));
        
        if (diffDays === 1) {
          newStreak += 1; // Practiced yesterday, increment
          totalDaysStudied += 1;
        } else if (diffDays > 1) {
          newStreak = 1; // Missed a day or more, reset to 1
          totalDaysStudied += 1;
        } else if (diffDays === 0 && newStreak === 0) {
          newStreak = 1; // Practiced today but had 0, set to 1
          totalDaysStudied += 1;
        }
      } else {
        newStreak = 1;
        totalDaysStudied += 1;
      }

      await update(userRef, {
        lastPractice: Date.now(),
        streak: newStreak,
        totalDaysStudied: totalDaysStudied,
        wordsLearned: (data.wordsLearned || 0) + (updateWordsLearned ? 1 : 0)
      })
    }

    // Record practice history for badges
    try {
      const todayDateStr = new Date().toISOString().split('T')[0];
      const practiceHistoryRef = ref(db, `users/${currentUser?.uid}/practiceHistory/${todayDateStr}`);
      const historySnapshot = await get(practiceHistoryRef);
      if (!historySnapshot.exists()) {
        await set(practiceHistoryRef, { timestamp: Date.now() });
      }
    } catch (err) {
      console.error('Error updating practiceHistory:', err);
    }

    if (updateWordsLearned) {
      try {
        const today = new Date().toDateString();
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();
        
        const todayProgressRef = ref(db, `users/${currentUser?.uid}/dailyProgress/${today}`);
        const todaySnapshot = await get(todayProgressRef);
        const todayData = todaySnapshot.exists() ? todaySnapshot.val() : { wordsLearned: 0 };
        await set(todayProgressRef, { ...todayData, wordsLearned: todayData.wordsLearned + 1 });

        const monthProgressRef = ref(db, `users/${currentUser?.uid}/monthlyProgress/${currentYear}/${currentMonth}`);
        const monthSnapshot = await get(monthProgressRef);
        const monthData = monthSnapshot.exists() ? monthSnapshot.val() : { wordsLearned: 0 };
        await set(monthProgressRef, { ...monthData, wordsLearned: monthData.wordsLearned + 1 });
      } catch (err) {
        console.error('Error updating daily/monthly progress:', err);
      }
    }
  }

  const saveUser = async (name, email, currentUser) => {
    if(!currentUser?.uid) return
    checkIfUserExistsAndCreate()
    const userRef = ref(db, `users/${currentUser?.uid}`);
    const snapshot = await get(userRef);
    if(snapshot.exists() === false) {
      const data = {
        wordsLearned: 0,
        lastPractice: Date.now(),
        email: email,
        username: name,
        totalDaysStudied: 0
      }
      await set(userRef, data)
    }

    const userRankingWeeklyRef = ref(db, `weekly/${currentUser?.uid}`);
    const snapshotRankingWeekly = await get(userRankingWeeklyRef);
    if(snapshotRankingWeekly.exists() === false) {
      const data = {
        score: 0,
        username: name,
      }
      await set(userRankingWeeklyRef, data)
    }

    const userRankingMonthlyRef = ref(db, `monthly/${currentUser?.uid}`);
    const snapshotRankingMonthly = await get(userRankingMonthlyRef);
    if(snapshotRankingMonthly.exists() === false) {
      const data = {
        score: 0,
        username: name,
      }
      await set(userRankingMonthlyRef, data)
    }

    const userRankingAllTimeRef = ref(db, `allTime/${currentUser?.uid}`);
    const snapshotRankingAllTime = await get(userRankingWeeklyRef);
    if(snapshotRankingAllTime.exists() === false) {
      const data = {
        score: 0,
        username: name,
      }
      await set(userRankingAllTimeRef, data)
    }
  }

  const getAllDayExercises = async (currentWord) => {
    if (!currentWord || !currentWord.timestamp) return false;
    checkIfUserExistsAndCreate()
    const wordRef = ref(db, `users/${currentUser?.uid}/wordOfTheDay`);
    const snapshot = await get(wordRef);
    const data = snapshot.val();
    if(data) {
      try {
      if(new Date(data.synonym.synonym).getTime() > new Date(currentWord.timestamp).getTime() &&
        new Date(data.fillBlank.fillBlank).getTime() > new Date(currentWord.timestamp).getTime() &&
        new Date(data.trueOrFalse.trueOrFalse).getTime() > new Date(currentWord.timestamp).getTime() && 
        new Date(data.speak.speak).getTime() > new Date(currentWord.timestamp).getTime()) {
        return true
      } else {
        return false
      }
      } catch(e) {
        return false
      }
    }
  }

  const checkAchievements = useCallback(async () => {
    try {
      if (!currentUser?.uid) return { achievements: [], badges: [], result: true };

      // Prevent multiple simultaneous calls
      if (hasCheckedAchievements.current) {
        console.log('checkAchievements: Already checked or checking, skipping');
        return { achievements: [], badges: [], result: true };
      }

      hasCheckedAchievements.current = true;
      console.log('checkAchievements: Starting achievement check for user:', currentUser.uid);
      
      const achievementService = AchievementService.getInstance(currentUser);
      
      // Get the actual user progress from Firebase
      const userRef = ref(db, `users/${currentUser?.uid}`);
      const snapshot = await get(userRef);
      const actualUserProgress = snapshot.exists() ? snapshot.val() : {};
      
      console.log('checkAchievements: Actual user progress from Firebase:', actualUserProgress);
      console.log('checkAchievements: Current userProgress state:', userProgress);
      
      // Use actual progress data for achievement checking
      const progressForAchievements = {
        wordsLearned: actualUserProgress.wordsLearned || 0,
        streak: actualUserProgress.streak || 0,
        lastPractice: actualUserProgress.lastPractice ? new Date(actualUserProgress.lastPractice) : new Date()
      };
      
      console.log('checkAchievements: Progress for achievement checking:', progressForAchievements);
      
      // Check for new achievements
      const newAchievements = await achievementService.checkAndUnlockAchievements(progressForAchievements, savedWords);
      const newBadges = await achievementService.checkAndUnlockBadges(progressForAchievements);
      
      console.log('checkAchievements: New achievements found:', newAchievements);
      console.log('checkAchievements: New badges found:', newBadges);
      
      // Update user achievements
      const updatedAchievements = await achievementService.getUserAchievements();
      console.log('checkAchievements: Updated user achievements:', updatedAchievements);
      setUserAchievements(updatedAchievements);
      console.log('checkAchievements: State updated, new userAchievements should be:', updatedAchievements);
      
      // Store new achievements for notifications
      if (newAchievements.length > 0 || newBadges.length > 0) {
        setNewAchievements({ achievements: newAchievements, badges: newBadges });
      }
      return { achievements: newAchievements, badges: newBadges, result: true };
    } catch (error) {
      console.error('checkAchievements: Error checking achievements:', error);
      return { achievements: [], badges: [], result: true };
    } finally {
      hasCheckedAchievements.current = false;
    }
  }, [currentUser?.uid, savedWords, userProgress]);

  const getDayExercise = async (exerciseName, currentWord) => {
    if (!currentWord || !currentWord.timestamp) return false;
    checkIfUserExistsAndCreate()
    const wordRef = ref(db, `users/${currentUser?.uid}/wordOfTheDay/${exerciseName}`);
    const snapshot = await get(wordRef);
    if(snapshot.exists() === false) {
      return false
    } else {
      const data = snapshot.val();
      if(Number(data[exerciseName]) > Number(currentWord.timestamp)) {
        return true
      } else {
        return false
      }
    }
  }

  const saveDayExercise = async (exerciseName: string) => {
    checkIfUserExistsAndCreate()
    const wordRef = ref(db, `users/${currentUser?.uid}/wordOfTheDay/${exerciseName}`);
    const snapshot = await get(wordRef);
    if(snapshot.exists() === false) {
      const data = {
        [exerciseName]: Date.now()
      }
      await set(wordRef, data)
    } else {
      const data = snapshot.val();
      await set(wordRef, {
        ...data,
        [exerciseName]: Date.now()
      })
    }
    
    // Update achievement metrics and lastPractice
    try {
      const userStatsRef = ref(db, `users/${currentUser?.uid}`);
      const userStatsSnapshot = await get(userStatsRef);
      if (userStatsSnapshot.exists()) {
        const userData = userStatsSnapshot.val();
        await set(userStatsRef, {
          ...userData,
          activitiesCompleted: (userData.activitiesCompleted || 0) + 1,
          totalPracticeTime: (userData.totalPracticeTime || 0) + 5,
          perfectScores: (userData.perfectScores || 0) + 1,
        });
      }
      
      // Update the user's streak and lastPractice time
      await updateUserProgress(false);
    } catch (err) {
      console.error('Error updating achievement metrics in saveDayExercise:', err);
    }
  }

  const getUserProgress = async () => {
    checkIfUserExistsAndCreate()
    const userLastPractice = ref(db, `users/${currentUser?.uid}`);
    const snapshot = await get(userLastPractice);
    const userData = snapshot.val();
    setUserProgress(userData)
  }

  const getWords = async () => {
    const wordRef = ref(db, `savedWords/${currentUser?.uid}`);
    const snapshot = await get(wordRef);
    const data = snapshot.val();
    setSavedWords(Object.values(data).sort((a, b) => b.createdAt - a.createdAt));
  }

  const getWordsFromTheme = async (theme: string) => {
    if (!currentUser?.uid) {
      console.error("User is not logged in.");
      return;
    }
  
    const themeRef = ref(db, `savedWords/${currentUser.uid}`);
    const wordQuery = query(themeRef, orderByChild("theme"), equalTo(theme));
  
    const expRef = ref(db, `savedExpressions/${currentUser.uid}`);
    const expQuery = query(expRef, orderByChild("theme"), equalTo(theme));
  
    try {
      const [wordSnapshot, expSnapshot] = await Promise.all([
        get(wordQuery),
        get(expQuery)
      ]);
  
      let allWords = [];
      
      if (wordSnapshot.exists()) {
        allWords = [...allWords, ...Object.values(wordSnapshot.val())];
      }
      
      if (expSnapshot.exists()) {
        allWords = [...allWords, ...Object.values(expSnapshot.val())];
      }
  
      if (allWords.length > 0) {
        setWordsTheme(allWords as Word[]);
      } else {
        console.log(`No words or expressions found with theme "${theme}".`);
        setWordsTheme([]);
      }
    } catch (error) {
      console.error("Error fetching words:", error);
    }
  };

  const getThemes = async () => {
    checkIfUserExistsAndCreate()
    const themeRef = ref(db, `users/${currentUser?.uid}/themes`);
    const snapshot = await get(themeRef);
    if(snapshot.exists() === false) return
    const data = snapshot.val();
    setThemesProgress(Object.values(data));
  }

  const deleteWord = async (wordId: string) => {
    if (!currentUser?.uid || !wordId) return;
    const safeKey = sanitizeFirebaseKey(wordId);
    if (!safeKey) return;
    const wordRef = ref(db, `savedWords/${currentUser?.uid}/${safeKey}`);
    await set(wordRef, null);
    setSavedWords((prev) => prev.filter((word) => word.word !== wordId && word.word !== safeKey));
    setAllSavedWords((prev) => prev.filter((word) => word.word !== wordId && word.word !== safeKey));
  }

  const deleteExpression = async (expressionId: string) => {
    if (!currentUser?.uid || !expressionId) return;
    const safeKey = sanitizeFirebaseKey(expressionId);
    if (!safeKey) return;
    const expRef = ref(db, `savedExpressions/${currentUser?.uid}/${safeKey}`);
    await set(expRef, null);
    setSavedExpressions((prev) => prev.filter((exp) => exp.word !== expressionId && exp.word !== safeKey));
    setAllSavedExpressions((prev) => prev.filter((exp) => exp.word !== expressionId && exp.word !== safeKey));
  }

  const updateWordTimestamp = async (wordId: string) => {
    if (!currentUser?.uid || !wordId) return;
    const safeKey = sanitizeFirebaseKey(wordId);
    if (!safeKey) return;
    const wordRef = ref(db, `savedWords/${currentUser?.uid}/${safeKey}`);
    const snapshot = await get(wordRef);
    if(snapshot.exists()) {
      const data = snapshot.val();
      data.createdAt = Date.now();
      data.lastPractice = Date.now();
      await set(wordRef, data);
      setSavedWords((prev) => {
        const updated = prev.map(w => (w.word === wordId || w.word === safeKey) ? { ...w, createdAt: data.createdAt, lastPractice: data.lastPractice } : w);
        return updated.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      });
      setAllSavedWords((prev) => {
        const updated = prev.map(w => (w.word === wordId || w.word === safeKey) ? { ...w, createdAt: data.createdAt, lastPractice: data.lastPractice } : w);
        return updated.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      });
    }
  }

const getWordsPaginated = async (loadMore = false) => {
    try {
        setLoading(true)
        if (!loadMore) {
            const wordRef = ref(db, `savedWords/${currentUser?.uid}`);
            const snapshot = await get(wordRef);
            const data = snapshot.val();
            
            if (data) {
                const filtered = Object.values(data).filter((w: any) => {
                    if (w.isAutoAdded !== undefined) return !w.isAutoAdded;
                    if (w.translation || w.type || w.literalMeaning) return true;
                    if (w.theme) return false;
                    return true;
                }).sort((a: any, b: any) => b.createdAt - a.createdAt);
                
                setAllSavedWords(filtered as Word[]);
                setSavedWords(filtered.slice(0, pageSize) as Word[]);
                setWordPage(1);
                setLoadMore(filtered.length > pageSize);
            } else {
                setAllSavedWords([]);
                setSavedWords([]);
                setLoadMore(false);
            }
        } else {
            const nextPage = wordPage + 1;
            const nextItems = allSavedWords.slice(0, nextPage * pageSize);
            setSavedWords(nextItems);
            setWordPage(nextPage);
            setLoadMore(allSavedWords.length > nextPage * pageSize);
        }
        setLoading(false);
    } catch (error) {
        console.error("Error retrieving words:", error);
    }
};

const getExpressionsPaginated = async (loadMore = false) => {
    try {
        setLoadingExpressions(true)
        if (!loadMore) {
            const expRef = ref(db, `savedExpressions/${currentUser?.uid}`);
            const snapshot = await get(expRef);
            const data = snapshot.val();
            
            if (data) {
                const filtered = Object.values(data).filter((w: any) => {
                    if (w.isAutoAdded !== undefined) return !w.isAutoAdded;
                    if (w.translation || w.type || w.literalMeaning) return true;
                    if (w.theme) return false;
                    return true;
                }).sort((a: any, b: any) => b.createdAt - a.createdAt);
                
                setAllSavedExpressions(filtered as Word[]);
                setSavedExpressions(filtered.slice(0, pageSize) as Word[]);
                setExpressionPage(1);
                setLoadMoreExpressions(filtered.length > pageSize);
            } else {
                setAllSavedExpressions([]);
                setSavedExpressions([]);
                setLoadMoreExpressions(false);
            }
        } else {
            const nextPage = expressionPage + 1;
            const nextItems = allSavedExpressions.slice(0, nextPage * pageSize);
            setSavedExpressions(nextItems);
            setExpressionPage(nextPage);
            setLoadMoreExpressions(allSavedExpressions.length > nextPage * pageSize);
        }
        setLoadingExpressions(false);
    } catch (error) {
        console.error("Error retrieving expressions:", error);
    }
};


  useEffect(() => {
      if(currentUser?.uid) {
        getWordsPaginated()
        getExpressionsPaginated()
        getUserProgress()
        getThemes()
        // Reset achievement check flag for new user
        hasCheckedAchievements.current = false;
      } else {
        setSavedWords([])
        setSavedExpressions([])
        setUserProgress({})
      }
  }, [currentUser])

  return (
    <UserContext.Provider
      value={{
        savedWords,
        savedExpressions,
        updateWordProgress,
        getWords,
        userProgress,
        addWord,
        addExpression,
        getWordsPaginated,
        getExpressionsPaginated,
        loadMore,
        loadMoreExpressions,
        loadingWords: loading,
        loadingExpressions,
        deleteWord,
        deleteExpression,
        updateWordTimestamp,
        getThemes,
        themesProgress,
        getWordsFromTheme,
        getWord,
        getExpression,
        wordsTheme,
        saveRateLimitAI,
        saveUser,
        wordOfDayPoints,
        setWordOfDayPoints,
        getDayExercise,
        saveDayExercise,
        getAllDayExercises,
        checkAchievements,
        userAchievements,
        newAchievements
      }}
    >
      {children}
    </UserContext.Provider>
  );
}